'use client';

import { ArchiveRestore, CalendarClock, Download, LifeBuoy, LoaderCircle, LockKeyhole, PackageOpen, ShieldAlert, X } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { mutationHeaders, responseMessage, type WorkspaceSummary } from './app-shell';

type DataExitRequestView = Readonly<{
  requestId: string;
  scope: 'WORKSPACE' | 'OWN_CONVERSATION';
  conversationId: string | null;
  status: 'REQUESTED' | 'PROCESSING' | 'READY' | 'EXPIRED' | 'FAILED' | 'CANCELLED';
  packageSizeBytes: string | null;
  packageExpiresAt: string | null;
  deliveredAt: string | null;
  fulfillmentDueAt: string;
  createdAt: string;
  updatedAt: string;
}>;

export function DataExitPanel({
  role,
  supportChannel,
  supportHours,
}: Readonly<{
  role: WorkspaceSummary['role'];
  supportChannel: string | null;
  supportHours: string;
}>) {
  const [requests, setRequests] = useState<readonly DataExitRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [earliestCleanup, setEarliestCleanup] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/data-exit-requests', { cache: 'no-store' });
      if (!response.ok) throw new Error(await responseMessage(response));
      const payload = await response.json() as { requests: readonly DataExitRequestView[] };
      setRequests(payload.requests);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '数据退出申请读取失败。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  async function createWorkspaceExitRequest(): Promise<void> {
    setPending('request');
    setMessage(null);
    setError(null);
    try {
      const response = await fetch('/api/data-exit-requests', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ scope: 'WORKSPACE' }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      setMessage('退出申请已创建，运营完成处理后可在这里下载。');
      await refresh();
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '退出申请创建失败。');
    } finally {
      setPending(null);
    }
  }

  async function downloadPackage(requestId: string): Promise<void> {
    setPending(`download:${requestId}`);
    setMessage(null);
    setError(null);
    try {
      const response = await fetch(`/api/data-exit-requests/${encodeURIComponent(requestId)}/download`, { cache: 'no-store' });
      if (!response.ok) throw new Error(await responseMessage(response));
      const payload = await response.json() as { url: string };
      const anchor = document.createElement('a');
      anchor.href = payload.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
      setMessage('已生成五分钟有效的下载链接。');
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '退出包下载失败。');
    } finally {
      setPending(null);
    }
  }

  async function closeWorkspace(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setPending('close');
    setMessage(null);
    setError(null);
    try {
      const response = await fetch('/api/workspace/close', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({
          confirmClose: form.get('confirmClose') === 'on',
          requestExit: form.get('requestExit') === 'on',
          ownerPassword: form.get('ownerPassword'),
        }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const payload = await response.json() as { closedAt: string };
      setCloseOpen(false);
      setEarliestCleanup(cleanupDateFrom(payload.closedAt));
      setMessage(`工作区已于 ${formatDateTime(payload.closedAt)} 关闭并进入只读保留期。`);
      await refresh();
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '工作区关闭失败。');
    } finally {
      setPending(null);
    }
  }

  return <>
    <section className="settings-section data-exit-section" aria-labelledby="data-exit-title">
      <div className="settings-section-heading">
        <ArchiveRestore size={20} aria-hidden="true" />
        <div><h2 id="data-exit-title">数据退出与关闭</h2><span>3 个工作日内处理 · 退出包 7 天有效 · 最早清理日期 {earliestCleanup ? formatDate(earliestCleanup) : '关闭后 90 天'}</span></div>
      </div>
      <p className="settings-note"><CalendarClock size={15} />关闭后停止新写入，现有资料进入 90 天只读保留期；待交付退出包和 LegalHold 会阻止清理。</p>
      <p className="settings-note"><LifeBuoy size={15} />支持渠道：{supportChannel ?? '暂未配置'} · {supportHours}</p>

      {role === 'OWNER' && <div className="data-exit-actions">
        <button className="secondary-button" type="button" disabled={pending !== null} onClick={() => void createWorkspaceExitRequest()}><PackageOpen size={16} />{pending === 'request' ? '申请中' : '申请工作区退出包'}</button>
        <button className="danger-button" type="button" disabled={pending !== null} onClick={() => {
          setEarliestCleanup(cleanupDateFrom(new Date().toISOString()));
          setCloseOpen(true);
        }}><ShieldAlert size={16} />关闭工作区</button>
      </div>}

      {loading ? <p className="settings-note"><LoaderCircle className="spin" size={15} />正在读取退出申请</p> : requests.length > 0 ? (
        <div className="data-exit-list" role="list">
          {requests.map((request) => <div className="data-exit-row" role="listitem" key={request.requestId}>
            <div className="data-exit-row-main">
              <div><strong>{request.scope === 'WORKSPACE' ? '工作区退出包' : '会话退出包'}</strong><span className={`status-badge ${request.status === 'READY' ? 'status-success' : request.status === 'FAILED' || request.status === 'EXPIRED' ? 'status-danger' : 'status-draft'}`}>{exitStatusLabel(request.status)}</span></div>
              <small>{formatDateTime(request.createdAt)} 申请 · 预计处理截止 {formatDateTime(request.fulfillmentDueAt)}{request.packageExpiresAt ? ` · ${formatDateTime(request.packageExpiresAt)} 到期` : ''}{request.packageSizeBytes ? ` · ${formatBytes(request.packageSizeBytes)}` : ''}</small>
            </div>
            {request.status === 'READY' && <button className="secondary-button" type="button" disabled={pending !== null} onClick={() => void downloadPackage(request.requestId)}><Download size={15} />{pending === `download:${request.requestId}` ? '生成中' : '下载退出包'}</button>}
          </div>)}
        </div>
      ) : <p className="settings-empty">当前没有可查看的数据退出申请。</p>}

      {message && <p className="form-success" role="status">{message}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
    </section>

    {closeOpen && earliestCleanup && <CloseWorkspaceDialog
      pending={pending === 'close'}
      earliestCleanup={earliestCleanup}
      onClose={() => setCloseOpen(false)}
      onSubmit={closeWorkspace}
    />}
  </>;
}

function CloseWorkspaceDialog({
  pending,
  earliestCleanup,
  onClose,
  onSubmit,
}: Readonly<{
  pending: boolean;
  earliestCleanup: string;
  onClose(): void;
  onSubmit(event: FormEvent<HTMLFormElement>): Promise<void>;
}>) {
  const passwordRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    passwordRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pending) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, pending]);

  return <div className="modal-backdrop" role="presentation">
    <section className="modal-panel close-workspace-dialog" role="dialog" aria-modal="true" aria-labelledby="close-workspace-title">
      <div className="modal-heading"><div><p className="eyebrow">不可立即恢复</p><h2 id="close-workspace-title">关闭工作区</h2></div><button className="icon-button" type="button" aria-label="关闭" title="关闭" disabled={pending} onClick={onClose}><X size={17} /></button></div>
      <p className="modal-copy">关闭后立即停止新制单与配置修改，最早于 {formatDate(earliestCleanup)} 进入清理流程。退出申请和法定保留仍会阻止删除。</p>
      <form className="settings-form" onSubmit={(event) => void onSubmit(event)}>
        <fieldset disabled={pending}>
          <div className="settings-fields">
            <label>Owner 密码<input ref={passwordRef} name="ownerPassword" type="password" required minLength={8} maxLength={200} autoComplete="current-password" /></label>
          </div>
          <label className="settings-check"><input name="requestExit" type="checkbox" defaultChecked />同时申请工作区退出包</label>
          <label className="settings-check"><input name="confirmClose" type="checkbox" required />我确认关闭后工作区进入只读保留期</label>
          <div className="modal-actions"><button className="secondary-button" type="button" disabled={pending} onClick={onClose}>取消</button><button className="danger-button" type="submit"><LockKeyhole size={16} />{pending ? '关闭中' : '确认关闭工作区'}</button></div>
        </fieldset>
      </form>
    </section>
  </div>;
}

function exitStatusLabel(status: DataExitRequestView['status']): string {
  return status === 'REQUESTED' ? '已申请' : status === 'PROCESSING' ? '处理中' : status === 'READY' ? '可下载' : status === 'EXPIRED' ? '已过期' : status === 'FAILED' ? '处理失败' : '已取消';
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('zh-CN');
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('zh-CN', { hour12: false });
}

function formatBytes(value: string): string {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '大小未知';
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function cleanupDateFrom(value: string): string {
  const date = new Date(value);
  date.setUTCDate(date.getUTCDate() + 90);
  return date.toISOString();
}
